<template>
  <validation-observer ref="question" v-slot="{ errors: errorsQuestion }">
    <ordered-header-card
      class="proposal-evaluation-question"
      :is-ordered="false"
      :order="questionOrder"
    >
      <div slot="content" class="proposal-evaluation-question__body">
        <p
          class="proposal-evaluation-question__body__question"
          v-html="questionName"
        />
        <div v-if="question.additionalQuestions.length > 0" class="row mb-2">
          <div
            v-for="(sub, index) in additionalFields"
            :key="sub.proposalEvaluationAdditionalQuestionId"
            class="col-12 mb-2"
            :class="{
              'col-md-6': question.additionalQuestions.length > 1,
            }"
          >
            <div class="row">
              <div class="col-12 custom-control-inline">
                <label class="mr-2">
                  {{ sub.additionalQuestionDescription }}
                </label>
                <validation-provider
                  v-slot="{ errors }"
                  class="input"
                  :name="
                    `section-${question.sectionId}-question-${question.questionId}-additionalQuestions-${sub.additionalQuestionId}`
                  "
                  rules="required"
                >
                  <custom-input
                    v-model="additionalFields[index].answer"
                    custom-error-msg=""
                    :disabled="readOnly"
                    :error="errors[0]"
                    type="text"
                    @input="emitTouched"
                  />
                </validation-provider>
              </div>
            </div>
          </div>
        </div>
        <error
          v-if="Object.values(errorsQuestion).flat(1)[0]"
          :message="customErrorMsg"
          variant="no-border"
        />
      </div>
      <div slot="actions" class="proposal-evaluation-question__actions">
        <validation-provider
          v-slot="{ errors }"
          :name="
            `section-${question.sectionId}-question-${question.questionId}`
          "
          rules="required"
        >
          <custom-radio
            :id="`rbtn-${question.questionId}-${Math.random()}`"
            v-model="selectedAnswer"
            :change-event="onSelectedAnswers"
            class="proposal-evaluation-question__custom-radio"
            :class="{
              'proposal-evaluation-question__custom-radio--error':
                errors[0] && !selectedAnswer,
            }"
            :custom-error-msg="' '"
            :custom-style="{
              separator: '0px',
              radioButtonWidth: '85px',
              centerBorderRadius: true,
            }"
            :disabled="readOnly"
            :error="false"
            :is-inline="true"
            name="evaluationQuestions"
            :options="radioOptions"
            type="questions"
          />
        </validation-provider>
      </div>
    </ordered-header-card>
  </validation-observer>
</template>

<script>
import OrderedHeaderCard from 'ordered-header-card';
import CustomRadio from 'custom-radio';
import CustomInput from 'CustomInput';
import sortBy from 'lodash/sortBy';
import Error from 'custom-error';

export default {
  name: 'ProposalEvaluationQuestion',
  components: {
    OrderedHeaderCard,
    CustomRadio,
    CustomInput,
    Error,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    radioOptions: [],
    additionalFields: [],
    selectedAnswer: null,
    value: null,
    questionName: '',
    questionOrder: '',
  }),
  computed: {
    additionalsAreEmpty() {
      return (
        this.additionalFields.length > 0 &&
        this.additionalFields.filter((x) => !x.answer).length > 0
      );
    },
    customErrorMsg() {
      let message = '';

      if (!this.seletedAnswer && this.additionalsAreEmpty) {
        message = 'Favor complete la información y seleccione una puntuación.';
      } else if (!this.seletedAnswer) {
        message = 'Favor seleccione una puntuación.';
      } else if (this.additionalsAreEmpty) {
        message = 'Favor llenar información.';
      }

      return message;
    },
  },
  watch: {
    value() {
      this.fillQuestion();
    },
    additionalFields: {
      deep: true,
      handler() {
        this.fillQuestion();
      },
    },
  },
  async created() {
    await this.initRadioOptions();
    await this.initAdditionalFields();
    await this.initQuestionName();
  },
  methods: {
    initQuestionName() {
      let name = this.question.questionDescription;
      let questionOrder = this.question.order;

      this.questionOrder = questionOrder;
      this.questionName = name;
    },
    initAdditionalFields() {
      if (!this.question.additionalQuestions) {
        this.additionalFields = [];
        return;
      }
      this.additionalFields = [...this.question.additionalQuestions];
    },
    async initRadioOptions() {
      const radioOptions = [];
      const options = sortBy(
        this.question.answers,
        (option) => option.answerQuestionValue
      ).reverse();

      for (let [index, option] of options.entries()) {
        radioOptions.push({
          id: option.answerId,
          name: option.answerQuestionValue,
          value: option.answerId,
          label: option.answerQuestionValue,
          check: false,
          invalid: false,
          disabled: false,
          colorSelected: this.getColor(index),
        });
      }
      this.radioOptions = [...radioOptions];

      const selectedOption = this.question.answers.find((x) => x.isSelected);

      if (selectedOption) {
        const selected = this.radioOptions.find(
          (x) => x.id == selectedOption.answerId
        );
        this.selectedAnswer = selected;
        this.onSelectedAnswers(selected, false);
      }
    },
    getColor(index) {
      const colors = ['#A3E5B3', '#F8EB7F', '#FAE7E7'];
      return colors[index];
    },
    onSelectedAnswers(item, emit = true) {
      this.value = this.question.answers.find((x) => x.answerId == item.id);

      if (emit) {
        this.emitTouched();
      }
    },
    fillQuestion() {
      if (this.value) {
        this.$emit('filled', {
          ...this.value,
          additionalFields: this.additionalFields,
        });
      }
    },
    emitTouched() {
      this.$emit('isTouched', true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

.proposal-evaluation-question ::v-deep {
  .h2 {
    font-size: 1.4286rem;
  }

  .custom-control-inline {
    .input {
      width: 6.25rem;
    }
  }

  @media only screen and (max-width: 768px) {
    .custom-control-inline {
      display: block;
      .input {
        width: 100%;
      }
    }
  }

  .square-order {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    text-align: center;
  }

  .proposal-evaluation-question__body {
    &__question {
      color: map-get($colors, 'black');
      font-weight: bold;
    }
  }

  .proposal-evaluation-question__custom-radio--error {
    .radio-button {
      border-color: map-get($colors, 'red');
      background-color: #fae7e7;

      &:nth-of-type(2) {
        border-left-color: transparent;
        border-right-color: transparent;
      }

      &:nth-of-type(1) {
        border-right-color: transparent;
      }

      &:nth-of-type(3) {
        border-left-color: transparent;
      }
    }
  }
}
</style>
